import React from 'react';
import { motion } from 'framer-motion';
import { TrendUp, TrendDown } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';

/**
 * Compact "Today's biggest mover" widget that picks the top gainer and top loser
 * from the user's CURRENT holdings (using each stock's daily change_percent).
 */
const MoverRow = ({ holding, isWinner, onClick }) => {
  const { format } = useCurrency();
  const Icon = isWinner ? TrendUp : TrendDown;
  const pct = parseFloat(holding?.change_percent ?? holding?.profit_loss_percent ?? 0);
  const colorCls = isWinner ? 'text-emerald-300' : 'text-pink-300';
  const bgCls = isWinner ? 'bg-emerald-500/10 border-emerald-400/20' : 'bg-pink-500/10 border-pink-400/20';

  return (
    <button
      onClick={onClick}
      className={`group w-full text-left p-3 rounded-xl border ${bgCls} hover:scale-[1.02] transition-transform`}
      data-testid={`top-mover-${isWinner ? 'gainer' : 'loser'}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">
          {isWinner ? "Best Performer" : "Worst Performer"}
        </span>
        <Icon size={14} weight="fill" className={colorCls} />
      </div>
      <div className="flex items-baseline justify-between">
        <span className="font-unbounded font-bold text-white text-lg truncate group-hover:text-gold transition-colors">
          {holding?.symbol || '—'}
        </span>
        <span className={`font-mono font-bold ${colorCls}`}>
          {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
        </span>
      </div>
      <p className="text-xs text-zinc-400 truncate mt-0.5">{holding?.company_name || holding?.name || ''}</p>
      {holding?.current_price !== undefined && (
        <p className="text-[10px] font-mono text-zinc-500 mt-0.5">{format(holding.current_price)}</p>
      )}
    </button>
  );
};

const TopMoversWidget = ({ holdings = [] }) => {
  const navigate = useNavigate();

  // Pick stock with biggest profit_loss_percent for "winner" and smallest (most negative) for "loser"
  if (!holdings || holdings.length === 0) {
    return (
      <div className="glass-card p-4 text-center text-xs text-zinc-500" data-testid="top-movers-empty">
        Trade to see your top movers
      </div>
    );
  }

  const sorted = [...holdings].sort((a, b) => (b.profit_loss_percent || 0) - (a.profit_loss_percent || 0));
  const gainer = sorted[0];
  const loser = sorted[sorted.length - 1];
  const sameAsset = sorted.length < 2;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="top-movers-widget">
      <MoverRow holding={gainer} isWinner={true} onClick={() => navigate(`/market/${gainer.symbol}`)} />
      {!sameAsset && (
        <MoverRow holding={loser} isWinner={false} onClick={() => navigate(`/market/${loser.symbol}`)} />
      )}
    </div>
  );
};

export default TopMoversWidget;
